import _ from 'lodash'
import { chainConfigs, ChainId, getBlockExplorerAddressLink, UdfFeed } from '../../config/appConfig'
import Asset from './Asset'
import assetsChainDetails from './assetsChainDetails.json'
import assetsCommon, { ICommonAssetDetails } from './assetsCommon'

export interface IAssetInfo extends ICommonAssetDetails {
  address: string
  decimals: number
}

/**
 * Get non-chain specific details about the asset + UI related config,
 * **throws** an error if asset not found.
 *
 * `{displayName, spriteLogo}`
 */
export function getCommon(asset: Asset) {
  const common = assetsCommon[asset]
  if (!common) {
    throw new Error(`${asset} token not found`)
  }
  return common
}

/**
 * Get chain specific details of an asset, **throws** an error if asset not found.
 */
export function getChainDetails(chainId: ChainId, asset: Asset) {
  // @ts-ignore
  const onChain: { address: string; decimals: number } = assetsChainDetails[chainId][asset]
  if (!onChain) {
    throw new Error(`${asset} token not found for chain ${chainId}`)
  }

  return onChain
}

/**
 * Get decimals of an asset, **throws** an error if asset not found.
 */
export function getDecimals(chainId: ChainId, asset: Asset) {
  // @ts-ignore
  const onChain: { address: string; decimals: number } = assetsChainDetails[chainId][asset]
  if (!onChain) {
    throw new Error(`${asset} token not found for chain ${chainId}`)
  }
  return onChain.decimals
}

/**
 * Get an asset common info + chain specific info, **throws** an error if asset not found.
 *
 * `{displayName, spriteLogo, address, decimals}`
 */
export function getInfo(chainId: ChainId, asset: Asset): IAssetInfo {
  // @ts-ignore
  const onChain: { address: string; decimals: number } = assetsChainDetails[chainId][asset]
  if (!onChain) {
    throw new Error(`${asset} token not found for chain ${chainId}`)
  }
  const common = assetsCommon[asset]
  return Object.assign({ ...onChain }, common)
}

export function getTokensInfo(chainId: ChainId, assets: Asset[]): IAssetInfo[] {
  return assets.map((asset) => getInfo(chainId, asset))
}

/**
 * Get address of an asset, **throws** an error if asset not found.
 */
export function getAddress(chainId: ChainId, asset: Asset) {
  // @ts-ignore
  const onChain: { address: string; decimals: number } = assetsChainDetails[chainId][asset]
  if (!onChain) {
    throw new Error(`${asset} token not found for chain ${chainId}`)
  }
  return onChain.address
}

export function getAddresses(chainId: ChainId, assets: Asset[]) {
  return assets.map((asset) => getAddress(chainId, asset))
}

/**
 * Find the asset corresponding to the address.
 * Returns Asset.UNKNOWN if not found.
 * eg: useful for gettings asset in blockchain events
 */
export function getAssetFromAddress(chainId: ChainId, address: string) {
  // TODO: fix a bug with this right now for Wrapped / Native tokens
  // eg: ETH is in the list of Asset with the WETH address. (ETH / WETH mix)
  // Consequence: this function can return ETH while it is expected to be WETH.
  const assetDetails = _.find(
    assetsChainDetails[chainId],
    (asset) => asset.address.toLowerCase() === address.toLowerCase()
  )
  return (assetDetails?.asset as Asset) || Asset.UNKNOWN
}

/**
 * Find the asset corresponding to the address.
 * Throws an error if the asset/address is not found.
 */
export function getAssetStrict(chainId: ChainId, address: string) {
  const asset = getAssetFromAddress(chainId, address)
  if (asset === Asset.UNKNOWN) {
    throw new Error(`No Matching Asset for ${address} on chain ${chainId}`)
  }
  return asset
}

/**
 * `getAssetByIToken(1, Asset.IUSDT) => Asset.USDT`
 * Throws an error if the asset is not found
 */
export function getAssetByIToken(chainId: ChainId, iTokenAsset: Asset): Asset {
  let underlying = iTokenAsset.substring(1) as Asset
  if (chainConfigs[chainId].native.asset === underlying) {
    underlying = chainConfigs[chainId].native.wrapped
  }
  // @ts-ignore
  const asset = assetsChainDetails[chainId][underlying].asset
  if (!asset) {
    throw new Error(`Asset not found ${asset}`)
  }
  return asset
}

/**
 * `getUnderlyingAssetFromItokenAddress(1, 0x1234...) => Asset.USDT`
 * where the address is the address of IUSDT
 */
export function getUnderlyingAssetFromItokenAddress(chainId: ChainId, iTokenAddress: string) {
  const iToken = getAssetStrict(chainId, iTokenAddress)
  return getAssetByIToken(chainId, iToken)
}

/**
 * Get address of iToken given underlying asset.
 * Throws error if asset is not found
 */
export function getITokenAddress(chainId: ChainId, underlyingAsset: Asset) {
  return getAddress(chainId, `I${underlyingAsset}` as Asset)
}

/**
 * Get addresses of multiple iTokens given underlying assets.
 * Throws error if asset is not found
 */
export function getITokenAddresses(chainId: ChainId, assets: Asset[]): string[] {
  return assets.map((asset) => getITokenAddress(chainId, asset))
}

/** Get itoken full info from underlying asset
 * Throws error if not found.
 */
export function getItokenInfo(chainId: ChainId, underlyingAsset: Asset) {
  const itoken = `I${underlyingAsset}` as Asset
  return getInfo(chainId, itoken)
}

export const stableCoins = [Asset.DAI, Asset.USDC, Asset.USDT, Asset.BUSD, Asset.MIM, Asset.FRAX]

export const isNativeTokenForNetwork = (chainId: ChainId, token: Asset) => {
  return token === chainConfigs[chainId].native.asset
}

export const getDefaultActiveToken = (
  chainId: ChainId,
  loanToken: Asset,
  collateralToken: Asset
): Asset => {
  const { stableCoins } = chainConfigs[chainId]
  const mainTokens = [Asset.ETH, Asset.WBTC, Asset.BTCB]
  let activeToken: Asset

  activeToken = stableCoins.concat(mainTokens).includes(collateralToken)
    ? loanToken
    : collateralToken
  if (mainTokens.includes(loanToken) && stableCoins.includes(collateralToken)) {
    activeToken = loanToken
  }
  if (mainTokens.includes(collateralToken) && stableCoins.includes(loanToken)) {
    activeToken = collateralToken
  }
  return activeToken
}

const wethToEth = (asset: Asset): Asset => {
  return asset === Asset.ETH || asset === Asset.WETH ? Asset.ETH : asset
}

const unwrapNativeAsset = (chainId: ChainId, asset: Asset): Asset => {
  return isNativeTokenForNetwork(chainId, asset) ? (asset.substring(1) as Asset) : asset
}

const wrapNativeAsset = (chainId: ChainId, asset: Asset): Asset => {
  return isNativeTokenForNetwork(chainId, asset) ? (`W${asset}` as Asset) : asset
}

const assetsMapForChart: Record<UdfFeed, Asset[]> = {
  KYBER: [Asset.WETH],
  BINANCE: [Asset.WETH, Asset.WBTC],
}

const checkWethToken = (udfFeed: UdfFeed, asset: Asset) => {
  return assetsMapForChart[udfFeed].includes(asset) ? (asset.substring(1) as Asset) : asset
}

export function getItokenAddressInfo(chainId: ChainId, asset: Asset) {
  try {
    // we need a try catch because the addition of STMATIC broke assumptions.
    // there is no itoken for STMATIC. TODO: need a nicer solution.
    const { address: iTokenAddress, decimals } = getItokenInfo(chainId, asset)
    const iTokenExplorerUrl = getBlockExplorerAddressLink(chainId, iTokenAddress)
    return { decimals, iTokenAddress, iTokenExplorerUrl }
  } catch (err) {
    return { decimals: 18, iTokenAddress: '', iTokenExplorerUrl: '' }
  }
}

export function isStablePair(pair: [Asset, Asset] | { baseToken: Asset; quoteToken: Asset }) {
  if (Array.isArray(pair)) {
    return stableCoins.includes(pair[0]) && stableCoins.includes(pair[1])
  }
  return stableCoins.includes(pair.baseToken) && stableCoins.includes(pair.quoteToken)
}

export default {
  checkWethToken,
  getAddress,
  getAddresses,
  getAssetByIToken,
  getAssetFromAddress,
  getAssetStrict,
  getChainDetails,
  getCommon,
  getDecimals,
  getDefaultActiveToken,
  getInfo,
  getITokenAddress,
  getITokenAddresses,
  getItokenInfo,
  getTokensInfo,
  getUnderlyingAssetFromItokenAddress,
  isNativeTokenForNetwork,
  isStablePair,
  stableCoins,
  unwrapNativeAsset,
  wethToEth,
  wrapNativeAsset,
}
